import React from 'react';
import { useField } from 'formik';

import { ErrorMessage } from '@components/shared/error';
import InputWrapper from '@components/shared/form/input-wrapper';
import Label from '@components/shared/label';
import StyledSelect from '@components/shared/select';
import { ErrorMessageError as E } from '@models/common/error';
import { FormSelectProps } from '@models/common/form';

const customStyles = {
  clearIndicator: () => ({
    display: 'none',
  }),

  multiValueRemove: () => ({
    display: 'none',
  }),

  input: () => ({
    position: 'absolute',
    opacity: '0',
    pointerEvents: 'none',
  }),

  indicatorSeparator: () => ({
    display: 'none',
  }),

  dropdownIndicator: (_provided: any, state: any) => ({
    cursor: 'pointer',
    marginTop: 4,
    transition: 'all .2s ease',
    transformOrigin: 'center',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
  }),

  multiValue: () => ({
    display: 'inline-block',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    background: 'transparent',
    padding: 0,
    margin: 0,
    paddingRight: '10px',
  }),

  multiValueLabel: () => ({
    paddingLeft: 0,
  }),

  control: (
    _provided: any,
    {
      selectProps: { rounded, bottomline },
    }: { selectProps: { rounded: boolean; bottomline: boolean } },
  ) => ({
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    outline: '0 !important',
    position: 'relative',
    transition: 'all 100ms',
    boxSizing: 'border-box',
    border: rounded ? '1px solid #CACACA' : '0px',
    borderRadius: rounded ? '30px' : '0px',
    borderBottom: bottomline ? '1px solid #CACACA' : '1px solid #172643',
    padding: rounded ? '5px 15px' : '5px 0px 5px 1px',
    minHeight: 40,
  }),

  singleValue: (
    _provided: any,
    { selectProps: { rounded } }: { selectProps: { rounded: boolean } },
  ) => ({
    maxWidth: rounded ? '83%' : 'calc(100% - 8px)',
  }),

  placeholder: () => ({
    fontFamily: 'proxima-nova,sans-serif',
    fontWeight: 300,
    color: '#737373',
  }),

  valueContainer: (
    _provided: any,
    {
      selectProps: { small, rounded },
    }: { selectProps: { small: boolean; rounded: boolean } },
  ) => ({
    display: 'flex',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: rounded ? '83%' : '92%',
    fontSize: small ? '20px' : '20px',
    letterSpacing: '0.17px',
    fontFamily: 'proxima-nova,sans-serif',
    fontWeight: 300,
  }),

  menu: () => ({
    // border: '1px solid #E1E1E1',
    borderRadius: '0px',
    marginBottom: '8px',
    marginTop: '8px',
    position: 'absolute',
    width: '100%',
    minWidth: 250,
    zIndex: '2',
    boxSizing: 'border-box',
    boxShadow: '0px 0px 0px 0px #000',
    backgroundColor: '#FFFFFF',
    top: '100%',
  }),

  option: () => ({
    margin: '0px 25px',
    padding: '18px 9px',
    display: 'block',
    cursor: 'pointer',
    background: '#ffffff',
    borderBottom: '1px solid #E1E1E1',
    fontSize: '20px',
  }),

  menuList: (provided: any) => ({
    border: '1px solid #E1E1E1',
    ...provided,
    maxHeight: 245,
    '::-webkit-scrollbar': {
      width: '4px',
      height: '0px',
      '-webkit-overflow-scrolling': 'auto !important',
    },
    '::-webkit-scrollbar-track': {
      background: '#fff',
    },
    '::-webkit-scrollbar-thumb': {
      background: '#172643',
      borderRadius: '6px',
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: '#172643',
    },
  }),
};

const Select: React.FunctionComponent<FormSelectProps> = ({
  label,
  containerStyles,
  options,
  rounded,
  small,
  ...props
}) => {
  const [field, meta, helpers] = useField(props);
  const { name, onChange, ...rest } = field;

  return (
    <InputWrapper css={[containerStyles]}>
      <Label rounded={rounded} small={small} label={label} name={name} />
      <StyledSelect
        rounded={rounded}
        small={small}
        id={name}
        inputId={name}
        instanceId={name}
        options={options}
        onChange={helpers.setValue as any}
        closeMenuOnSelect={false}
        styles={customStyles as any}
        {...rest}
        {...props}
      />
      {meta.touched && meta.error && <ErrorMessage error={meta.error as E} />}
    </InputWrapper>
  );
};

export default Select;
